import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Store as StoreIcon, ShieldCheck, Sparkles, Check, Loader2, Copy, ArrowUpRight, ChevronDown, Info } from "lucide-react";
import { api, errMsg, DELIVERY, YESNO, EVIDENCE_TYPES, RESPONSIBILITY, FINAL_REC, REPLY_TONES, strengthLabel, charLimit } from "@/lib/api";
import { PageHeader, Panel, ScoreRing, FillGuide } from "@/components/Shared";
import { Field, Select, Segmented, inputCls, areaCls } from "@/components/Form";
import { ReplyResult } from "@/components/ReplyResult";
import { EvidenceDrop } from "@/components/EvidenceDrop";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { store_id: "", platform: "99food", occurrence_type: "cancelamento", order_number: "", customer_comment: "", complementary_info: "", platform_reason: "", store_explanation: "", delivery_by: "parceiro", sealed_package: "nao_informado", photo_before_pickup: "nao_informado", financial_value: "", reply_tone: "auto" };
const TYPES = [
  { key: "cancel", title: "Cancelamento ou reembolso", sub: "Responsabilidade, pedido, expedição e entrega." },
  { key: "avaliacao", title: "Avaliação negativa", sub: "Relevância, especificações e moderação." },
  { key: "resposta", title: "Resposta ao comentário", sub: "Agradeça elogios ou responda publicamente." },
];
const TYPE_VALUE = { cancel: "cancelamento", avaliacao: "avaliacao", resposta: "resposta" };
const isCancelType = (t) => t === "cancelamento" || t === "reembolso";
const STRATEGY = ["Confronta a reclamação com as provas", "Usa palavras-chave e princípios aplicáveis", "Pede revisão, correção ou remoção quando houver fundamento", "Mostra o que ainda pode aumentar a chance"];

function DefenseResult({ c, limit }) {
  const a = c.analysis;
  const pct = Math.round(a.forca * 10);
  const copy = () => { navigator.clipboard.writeText(c.contest_text); toast.success("Defesa copiada! Cole na plataforma."); };
  return (
    <div className="fade-up rounded-2xl border border-slate-200 bg-white p-5" data-testid="defense-result">
      <div className="flex items-center gap-4">
        <ScoreRing pct={pct} size={84} stroke={6} label="estimada" testid="defense-score" />
        <div><span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-900">{strengthLabel(pct)}</span><p className="mt-2 font-display font-bold text-slate-900">{a.titulo || "Defesa formulada"}</p><p className="text-xs text-slate-500">{c.case_code} · {FINAL_REC[a.recomendacao_final]}</p></div>
      </div>
      <div className="mt-4 grid gap-2">
        {a.ponto_principal_defesa && <div className="rounded-xl bg-emerald-50/70 px-3 py-2.5"><p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">Ponto principal da defesa</p><p className="text-[13px] text-slate-800" data-testid="defense-main-point">{a.ponto_principal_defesa}</p></div>}
        <FillGuide platform={c.platform} occurrence={c.occurrence_type} motivo={a.motivo_recomendado} submotivo={a.submotivo_recomendado} />
        <div className="rounded-xl bg-slate-50 px-3 py-2.5"><p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Responsabilidade</p><p className="text-[13px] font-semibold text-slate-900">{RESPONSIBILITY[a.responsabilidade] || a.responsabilidade}</p></div>
      </div>
      <p className="mb-1.5 mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Texto da defesa</p>
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-sm leading-relaxed text-slate-800" data-testid="defense-text">{c.contest_text}</div>
      <div className="mt-2 flex items-center justify-between"><span className="font-mono text-[11px] text-slate-500" data-testid="defense-char-count">{c.contest_text.length}/{limit} caracteres</span>
        <button data-testid="defense-copy-btn" onClick={copy} className="ai-cta inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white"><Copy className="h-4 w-4" /> COPIAR</button></div>
      {a.documentos_recomendados?.length > 0 && (
        <div className="mt-4"><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Pode aumentar a chance</p>
          <ul className="mt-1.5 space-y-1">{a.documentos_recomendados.slice(0, 3).map((x) => <li key={x} className="flex gap-2 text-xs text-slate-600"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />{x}</li>)}</ul></div>
      )}
      {a.risco_negativa && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/50 px-3 py-2.5"><p className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-700">Risco de negativa</p><p className="text-xs text-slate-700" data-testid="defense-risk">{a.risco_negativa}</p></div>}
      <Link to={`/recursos/${c.case_id}`} data-testid="open-full-case" className="mt-5 flex h-11 items-center justify-center gap-1 rounded-xl border border-slate-300 text-sm font-semibold text-slate-900 hover:bg-slate-50">Abrir caso completo <ArrowUpRight className="h-4 w-4" /></Link>
      <p className="mt-3 flex gap-1.5 text-[10px] text-slate-500"><Info className="h-3 w-3 shrink-0" /> Estimativa baseada nas informações inseridas. Não representa garantia de aprovação.</p>
    </div>
  );
}

const PLATFORM_OPTS = { "99food": "99Food", ifood: "iFood", keeta: "Keeta", outra: "Outra plataforma" };
const SUBTYPES = [["cancelamento", "Cancelamento"], ["reembolso", "Reembolso"]];

async function uploadEvidences(groups) {
  const evidences = [];
  for (const [group, list] of groups) {
    for (const it of list) {
      const fd = new FormData(); fd.append("file", it.file); fd.append("evidence_type", it.type);
      const { data } = await api.post("/files", fd);
      evidences.push({ file_id: data.file_id, type: EVIDENCE_TYPES[it.type], group });
    }
  }
  return evidences;
}

function DefenseTypePanel({ f, setF }) {
  const isCancel = isCancelType(f.occurrence_type);
  return (
    <Panel title="Tipo de defesa" sub="A estratégia muda conforme o caso." icon={FileText}>
      <div className="grid gap-3 sm:grid-cols-3">
        {TYPES.map((t) => {
          const active = t.key === "cancel" ? isCancel : f.occurrence_type === TYPE_VALUE[t.key];
          return (
            <button key={t.key} type="button" data-testid={`defense-type-${t.key}`} onClick={() => setF({ ...f, occurrence_type: TYPE_VALUE[t.key] })}
              className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-emerald-600/50 bg-emerald-50/60 ring-1 ring-emerald-600/30" : "border-slate-200 hover:border-slate-300"}`}>
              <p className="text-sm font-semibold text-slate-900">{t.title}</p><p className="mt-0.5 text-xs text-slate-500">{t.sub}</p>
            </button>
          );
        })}
      </div>
      {isCancel && (
        <div className="mt-3 flex gap-2" data-testid="cancel-subtype">
          {SUBTYPES.map(([k, v]) => (
            <button key={k} type="button" data-testid={`cancel-subtype-${k}`} onClick={() => setF({ ...f, occurrence_type: k })} className={`h-9 rounded-full px-4 text-xs font-semibold ${f.occurrence_type === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{v}</button>
          ))}
        </div>
      )}
    </Panel>
  );
}

function OrderDetails({ f, set, stores }) {
  const [more, setMore] = useState(false);
  const storeOpts = useMemo(() => ({ "": "Sem loja vinculada", ...Object.fromEntries(stores.map((s) => [s.store_id, s.name])) }), [stores]);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white">
      <button type="button" data-testid="toggle-order-details" onClick={() => setMore(!more)} className="flex w-full items-center justify-between p-5 text-left">
        <div><p className="font-display font-bold text-slate-900">Detalhes do pedido <span className="font-normal text-slate-400">(opcional, deixa a defesa mais precisa)</span></p><p className="text-xs text-slate-500">Número do pedido, valor, entrega, lacre e explicação da loja.</p></div>
        <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${more ? "rotate-180" : ""}`} />
      </button>
      {more && (
        <div className="space-y-4 border-t border-slate-100 p-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Número do pedido"><input data-testid="order-number-input" className={inputCls} value={f.order_number} onChange={set("order_number")} /></Field>
            <Field label="Valor envolvido (R$)"><input data-testid="financial-value-input" type="number" min="0" step="0.01" className={inputCls} value={f.financial_value} onChange={set("financial_value")} /></Field>
            <Field label="Loja / cardápio"><Select testid="store-select" value={f.store_id} onChange={set("store_id")} options={storeOpts} /></Field>
          </div>
          <Field label="Texto da reclamação do cliente (se quiser digitar além do print)"><textarea data-testid="customer-comment-input" rows={2} className={areaCls} value={f.customer_comment} onChange={set("customer_comment")} placeholder="Cole o texto da avaliação ou solicitação de cancelamento..." /></Field>
          <Field label="Motivo informado pela plataforma"><input data-testid="platform-reason-input" className={inputCls} value={f.platform_reason} onChange={set("platform_reason")} placeholder="Ex.: Item faltando" /></Field>
          <Field label="Explicação da loja"><textarea data-testid="store-explanation-input" rows={3} className={areaCls} value={f.store_explanation} onChange={set("store_explanation")} placeholder="O que a loja registrou/fez neste pedido" /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Quem realizou a entrega"><Select testid="delivery-by-select" options={DELIVERY} value={f.delivery_by} onChange={set("delivery_by")} /></Field>
            <Field label="Embalagem lacrada?"><Select testid="sealed-select" options={YESNO} value={f.sealed_package} onChange={set("sealed_package")} /></Field>
            <Field label="Foto antes da coleta?"><Select testid="photo-before-select" options={YESNO} value={f.photo_before_pickup} onChange={set("photo_before_pickup")} /></Field>
          </div>
        </div>
      )}
    </section>
  );
}

function DefenseSide({ result, busy, limit }) {
  return (
    <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
      <div className="rounded-2xl bg-slate-900 p-5">
        <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400"><Sparkles className="h-3.5 w-3.5" /> Defesa estratégica</p>
        <ul className="mt-3 space-y-2">{STRATEGY.map((s) => <li key={s} className="flex gap-2 text-xs text-white/80"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/60" />{s}</li>)}</ul>
        <p className="mt-4 rounded-lg bg-white/10 px-3 py-2 text-xs text-white" data-testid="char-limit-hint">Limite desta plataforma: <b>{limit} caracteres</b></p>
      </div>
      {result?.analysis ? (result.occurrence_type === "resposta" ? <ReplyResult c={result} /> : <DefenseResult c={result} limit={limit} />) : (
        <div className={`rounded-2xl border border-dashed border-slate-300 p-5 ${busy ? "scan-line" : ""}`} data-testid="defense-placeholder">
          <p className="font-display font-bold text-slate-900">{busy || "O resultado aparecerá aqui"}</p>
          <p className="mt-1 text-xs text-slate-500">Os dois grupos serão confrontados para formular o recurso para a plataforma.</p>
        </div>
      )}
    </aside>
  );
}

export default function NewCase() {
  const { refresh } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [complaint, setComplaint] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [stores, setStores] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isCancel = isCancelType(f.occurrence_type);
  const isReply = f.occurrence_type === "resposta";

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const submit = async () => {
    if (isReply && !complaint.length && !f.customer_comment.trim()) return toast.error("Anexe o print do comentário ou digite o comentário do cliente.");
    if (!complaint.length && !f.complementary_info.trim() && !f.customer_comment.trim()) return toast.error("Anexe o print da reclamação ou preencha as informações complementares.");
    setResult(null);
    try {
      setBusy("Enviando anexos...");
      const evidences = await uploadEvidences(isReply ? [["reclamacao", complaint]] : [["reclamacao", complaint], ["loja", proofs]]);
      setBusy(isReply ? "Criando resposta ao cliente..." : "Formulando resposta de defesa...");
      const { data: c } = await api.post("/cases", { ...f, store_id: f.store_id || null, financial_value: Number(f.financial_value || 0), evidences });
      const { data } = await api.post(`/cases/${c.case_id}/analyze`);
      setResult(data);
      refresh();
      setTimeout(() => document.querySelector('[data-testid="defense-result"], [data-testid="reply-result"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      toast.success(isReply ? "Resposta criada!" : "Defesa formulada!");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const limit = charLimit(cfg, f.platform, f.occurrence_type);

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Novo caso" title="Monte sua defesa" desc="Anexe o que o cliente escreveu, adicione as provas do restaurante e receba a defesa mais forte possível." />
      <div className="grid gap-5 lg:grid-cols-[1fr_330px]">
        <div className="space-y-5">
          <DefenseTypePanel f={f} setF={setF} />

          <Panel title={isReply ? "Comentário do cliente" : isCancel ? "Solicitação de cancelamento ou reembolso" : "Avaliação do cliente"} sub={isReply ? "Positivo ou negativo: criamos a resposta pública ideal." : "Este é o material que será contestado."} icon={StoreIcon}>
            <div className="space-y-4">
              <Field label="Plataforma"><Select testid="platform-select" options={PLATFORM_OPTS} value={f.platform} onChange={set("platform")} /></Field>
              {isReply && (
                <>
                  <Field label="Comentário do cliente"><textarea data-testid="reply-comment-input" rows={3} className={areaCls} value={f.customer_comment} onChange={set("customer_comment")} placeholder="Cole o comentário do cliente (ou anexe o print abaixo)" /></Field>
                  <Field label="Tom da resposta"><Segmented testid="reply-tone-select" options={REPLY_TONES} value={f.reply_tone} onChange={(v) => setF({ ...f, reply_tone: v })} /></Field>
                </>
              )}
              <div>
                <div className="mb-1.5 flex justify-between"><span className="text-sm font-semibold text-slate-900">Informações complementares</span><span className="text-[11px] text-slate-400">Ajudam a formar o argumento</span></div>
                <textarea data-testid="complementary-info-input" rows={4} className={areaCls} value={f.complementary_info} onChange={set("complementary_info")} placeholder="Ex.: A loja não comercializa mandioca frita. O pedido saiu lacrado com 4 lanches e 1 Coca-Cola, conferido e fotografado antes da coleta. Entrega feita por parceiro da 99." />
                <p className="mt-1 text-[11px] text-slate-500">Conte o que a loja sabe sobre o pedido: o que foi enviado, o que não vende, como foi embalado, quem entregou. Isso é usado para montar a contestação.</p>
              </div>
              <EvidenceDrop testid="complaint-drop" label={isReply ? "Print do comentário" : "Anexo da reclamação"} hint={isReply ? "Print do comentário e das estrelas dadas pelo cliente." : "Print da avaliação, estrelas ou solicitação de cancelamento feita pelo cliente."} emptyTitle={isReply ? "Anexe o comentário do cliente" : "Anexe a avaliação ou solicitação"} files={complaint} setFiles={setComplaint} defaultType={isCancel ? "print_cancelamento" : "print_avaliacao"} />
            </div>
          </Panel>

          {!isReply && (
            <>
              <Panel title="Provas do restaurante" sub="Anexe tudo que possa fortalecer a reversão." icon={ShieldCheck}>
                <EvidenceDrop testid="proof-drop" label="Anexos de prova" hint="Fotos, prints, comprovantes, registros de expedição e documentos do restaurante." emptyTitle="Anexe as provas do restaurante" files={proofs} setFiles={setProofs} defaultType="foto_pedido" />
              </Panel>
              <OrderDetails f={f} set={set} stores={stores} />
            </>
          )}

          <button data-testid="analyze-ai-btn" disabled={!!busy} onClick={submit} className={`ai-cta flex h-14 w-full items-center justify-center gap-3 rounded-2xl font-display text-base font-extrabold tracking-wide text-white disabled:opacity-80 ${busy ? "scan-line" : ""}`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />} {busy || (isReply ? "CRIAR RESPOSTA" : "FORMULAR DEFESA")}
          </button>
        </div>

        <DefenseSide result={result} busy={busy} limit={limit} />
      </div>
    </div>
  );
}
